import {
  Checkbox,
  ELinkVariant,
  ETextStyleVariant,
  Link,
  RadioSelector,
  SelectorGroup,
  Text,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React, { ReactNode, useCallback, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { AddressInput } from '@/components/common/AddressInput';
import { ILocationSearchAutocompleteListOption } from '@/hooks/useLocationSearchAutocompleteOptions';
import { setMapboxPlace } from '@/redux/modules/mapboxPlace';
import { retrieveMapboxFullAddress } from '@/services/retrieveMapboxFullAddress';
import { EDeliveryOption } from '@/services/types/core/delivery.types';

import { IDeliveryInfo } from './types';

interface IDeliverySelectorProps {
  onDeliveryChange: (info: IDeliveryInfo) => void;
  initialDeliveryInfo?: IDeliveryInfo;
  centerTitle?: boolean;
}

const DeliverySelector: React.FC<IDeliverySelectorProps> = ({
  onDeliveryChange,
  initialDeliveryInfo,
  centerTitle,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [deliveryAddress, setDeliveryAddress] = useState<string>(
    initialDeliveryInfo?.address || '',
  );
  const [deliveryStationary, setDeliveryStationary] = useState<EDeliveryOption | undefined>(
    initialDeliveryInfo?.deliveryStationary,
  );
  const [stationaryAcknowledged, setStationaryAcknowledged] = useState<boolean>(
    initialDeliveryInfo?.stationaryAcknowledged || false,
  );
  const [mapboxId, setMapboxId] = useState<string | undefined>(initialDeliveryInfo?.mapboxId);
  const [deliveryCenter, setDeliveryCenter] = useState<string | undefined>(
    initialDeliveryInfo?.deliveryCenter,
  );
  const [deliveryDetails, setDeliveryDetails] = useState<string | undefined>(
    initialDeliveryInfo?.deliveryDetails,
  );

  const latestDeliveryRef = useRef({
    address: initialDeliveryInfo?.address || '',
    mapboxId: initialDeliveryInfo?.mapboxId,
    deliveryCenter: initialDeliveryInfo?.deliveryCenter,
    deliveryDetails: initialDeliveryInfo?.deliveryDetails,
  });

  const handleAddressChange = useCallback(
    (inputValue: string) => {
      setDeliveryAddress(inputValue);
      latestDeliveryRef.current = {
        ...latestDeliveryRef.current,
        address: inputValue,
      };
      onDeliveryChange({
        address: inputValue,
        deliveryStationary,
        stationaryAcknowledged,
        mapboxId,
        deliveryCenter,
        deliveryDetails,
      });
    },
    [
      onDeliveryChange,
      deliveryStationary,
      stationaryAcknowledged,
      mapboxId,
      deliveryCenter,
      deliveryDetails,
    ],
  );

  const handleSelectAddress = useCallback(
    async (option: ILocationSearchAutocompleteListOption) => {
      if (!option) return;

      const { label, value } = option;
      const selectedAddress = value?.name || value?.address || label;

      setDeliveryAddress(selectedAddress);

      const optionMapboxId = value?.mapbox_id || value?.id;
      let fullMapboxAddress = '';

      if (optionMapboxId) {
        fullMapboxAddress = await retrieveMapboxFullAddress(value);
      }

      if (value) {
        dispatch(setMapboxPlace(value));
      }

      const newMapboxId = optionMapboxId || mapboxId;
      const newDeliveryCenter = fullMapboxAddress
        ? JSON.stringify({
            lat: value?.center?.[1],
            lng: value?.center?.[0],
          })
        : deliveryCenter;
      const newDeliveryDetails = fullMapboxAddress || deliveryDetails;

      setMapboxId(newMapboxId);
      setDeliveryCenter(newDeliveryCenter);
      setDeliveryDetails(newDeliveryDetails);

      latestDeliveryRef.current = {
        address: fullMapboxAddress || selectedAddress,
        mapboxId: newMapboxId,
        deliveryCenter: newDeliveryCenter,
        deliveryDetails: newDeliveryDetails,
      };

      onDeliveryChange({
        address: fullMapboxAddress || selectedAddress,
        deliveryStationary,
        stationaryAcknowledged,
        mapboxId: newMapboxId,
        deliveryCenter: newDeliveryCenter,
        deliveryDetails: newDeliveryDetails,
      });
    },
    [
      dispatch,
      onDeliveryChange,
      deliveryStationary,
      stationaryAcknowledged,
      mapboxId,
      deliveryCenter,
      deliveryDetails,
    ],
  );

  const handleSelectRadio = useCallback(
    (value: string) => {
      const option = value as EDeliveryOption;
      setDeliveryStationary(option);
      // Reset stationary acknowledged when switching options
      const acknowledged = option === EDeliveryOption.STATIONARY ? false : stationaryAcknowledged;
      setStationaryAcknowledged(acknowledged);

      const latest = latestDeliveryRef.current;
      onDeliveryChange({
        address: latest.address || deliveryAddress,
        deliveryStationary: option,
        stationaryAcknowledged: acknowledged,
        mapboxId: latest.mapboxId || mapboxId,
        deliveryCenter: latest.deliveryCenter || deliveryCenter,
        deliveryDetails: latest.deliveryDetails || deliveryDetails,
      });
    },
    [
      onDeliveryChange,
      deliveryAddress,
      stationaryAcknowledged,
      mapboxId,
      deliveryCenter,
      deliveryDetails,
    ],
  );

  const handleStationaryAcknowledgedChange = useCallback(
    (value: boolean) => {
      setStationaryAcknowledged(value);
      const latest = latestDeliveryRef.current;
      onDeliveryChange({
        address: latest.address || deliveryAddress,
        deliveryStationary,
        stationaryAcknowledged: value,
        mapboxId: latest.mapboxId || mapboxId,
        deliveryCenter: latest.deliveryCenter || deliveryCenter,
        deliveryDetails: latest.deliveryDetails || deliveryDetails,
      });
    },
    [
      onDeliveryChange,
      deliveryAddress,
      deliveryStationary,
      mapboxId,
      deliveryCenter,
      deliveryDetails,
    ],
  );

  return (
    <div className="p-4 mt-6 text-left border rounded-lg bg-neutral-05 border-neutral-20">
      <Text
        variant={ETextStyleVariant.LargeBold}
        className={clsx('mb-5 text-base font-medium semiHighlight text-primary-base', {
          'text-center': centerTitle,
        })}>
        <FormattedMessage
          id="F/DrkX"
          defaultMessage="Enter your delivery details."
          description="Guided search > Delivery selector > Title"
        />
      </Text>

      <div className="mb-6">
        <AddressInput
          inputValue={deliveryAddress}
          onChange={handleAddressChange}
          onSelect={handleSelectAddress}
          label={intl.formatMessage({
            defaultMessage: 'Delivery location',
            id: 'Flx2cd',
          })}
        />
      </div>

      <Text variant={ETextStyleVariant.MediumRegular} className="mb-4">
        <FormattedMessage
          defaultMessage="Will you be driving the RV during your trip?"
          id="OwVnU9"
        />
      </Text>

      <div className="mb-4">
        <SelectorGroup
          ariaLabel={intl.formatMessage({
            defaultMessage: 'Will you be driving the RV during your trip?',
            id: 'OwVnU9',
          })}
          value={deliveryStationary || ''}
          onChange={(value: string) => handleSelectRadio(value)}>
          <div className="flex flex-col gap-4">
            <RadioSelector
              ariaLabel={intl.formatMessage({
                defaultMessage: 'Yes, I plan to drive',
                id: 'mxA6k1',
              })}
              name="guided-search-driving-option"
              value={EDeliveryOption.MOVING}
              label={intl.formatMessage({
                defaultMessage: 'Yes, I plan to drive',
                id: 'mxA6k1',
              })}
              description={intl.formatMessage({
                defaultMessage:
                  'Includes delivery to your location so you can hit the road after drop-off.',
                id: 'C0AjR6',
              })}
              withBorder
              showIndicator
              className="flex-1"
            />
            <RadioSelector
              ariaLabel={intl.formatMessage({
                defaultMessage: 'No, it will stay parked',
                id: 'n5+Rhk',
              })}
              name="guided-search-driving-option"
              value={EDeliveryOption.STATIONARY}
              label={intl.formatMessage({
                defaultMessage: 'No, it will stay parked',
                id: 'n5+Rhk',
              })}
              description={intl.formatMessage({
                defaultMessage:
                  "The host will set everything up at your campsite or location. Since you aren't driving, we'll discount your insurance.",
                id: '0Wy0pq',
              })}
              withBorder
              showIndicator
              className="flex-1"
            />
          </div>
        </SelectorGroup>
      </div>

      {deliveryStationary === EDeliveryOption.STATIONARY && (
        <Checkbox
          ariaLabel={intl.formatMessage({
            defaultMessage: 'I acknowledge that moving a stationary rental voids my insurance.',
            id: 'VJHMXk',
          })}
          content={{
            label: (
              <>
                {intl.formatMessage(
                  {
                    defaultMessage:
                      'I acknowledge that moving a stationary rental voids my insurance. <link>Learn more about stationary delivery.</link>',
                    id: 'rTi8qJ',
                  },
                  {
                    link: (text: ReactNode) => (
                      <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        href="/blog/stationary-rentals"
                        variant={ELinkVariant.Underline}>
                        {text}
                      </Link>
                    ),
                  },
                )}
                <span aria-hidden className="ml-1 text-red-800">
                  *
                </span>
              </>
            ),
          }}
          value="acknowledged"
          isSelected={stationaryAcknowledged}
          onChange={handleStationaryAcknowledgedChange}
        />
      )}
    </div>
  );
};

export default DeliverySelector;
